import { CreateUser, UpdateUser, UsersEntity } from '@crm/dto';
import { ApiResponseInterface, ApiResponseStatus, UserStatus } from '@crm/interface';
import { API_RESPONSE, prepareUpdateFields } from '@crm/utils';
import { Injectable, Logger } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { Repository } from 'typeorm';

@Injectable()
export class UsersService {
  private readonly logger = new Logger(UsersService.name);

  constructor(
    @InjectRepository(UsersEntity)
    private readonly usersRepo: Repository<UsersEntity>,
  ) {}

  // ====== GET ALL USERS ======
  async getAll(): Promise<ApiResponseInterface> {
    try {
      const users = await this.usersRepo.find({
        relations: [
          'created_by',
          'updated_by',
          'attachment_signature',
          'attachment_avatar',
        ],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.USERS.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.USERS.GET_ALL.MESSAGE,
        data: users,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.USERS.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.USERS.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // ====== GET USER BY CREATOR/USER ID ======
  async getByUserId(userId: string): Promise<ApiResponseInterface> {
    try {
      const users = await this.usersRepo.find({
        where: { created_by: { id: userId } },
        relations: [
          'created_by',
          'updated_by',
          'attachment_signature',
          'attachment_avatar',
        ],
      });

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.USERS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.USERS.GET_BY_ID.MESSAGE,
        data: users,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.USERS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.USERS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // ====== GET USER BY ID ======
  async getById(id: string): Promise<ApiResponseInterface> {
    try {
      const user = await this.usersRepo.findOne({
        where: { id },
        relations: [
          'created_by',
          'updated_by',
          'attachment_signature',
          'attachment_avatar',
        ],
      });

      if (!user) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.USERS.GET_BY_ID.CODE,
          message: API_RESPONSE.ERROR.USERS.GET_BY_ID.MESSAGE,
          data: null,
        };
      }

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.USERS.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.USERS.GET_BY_ID.MESSAGE,
        data: user,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.USERS.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.USERS.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // ====== CREATE USER ======
  async create(dto: CreateUser): Promise<ApiResponseInterface> {
    try {
      const createData: any = { ...dto };

      // Map relations
      if (dto.created_by_id) createData.created_by = { id: dto.created_by_id };
      if (dto.updated_by_id) createData.updated_by = { id: dto.updated_by_id };
      if (dto.attachment_signature_id)
        createData.attachment_signature = { id: dto.attachment_signature_id };
      if (dto.attachment_avatar_id)
        createData.attachment_avatar = { id: dto.attachment_avatar_id };

      // Remove raw ID fields
      delete createData.created_by_id;
      delete createData.updated_by_id;
      delete createData.attachment_signature_id;
      delete createData.attachment_avatar_id;

      const newUser = this.usersRepo.create(createData);
      await this.usersRepo.save(newUser);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.USERS.CREATE.CODE,
        message: API_RESPONSE.SUCCESS.USERS.CREATE.MESSAGE,
        data: newUser,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.USERS.CREATE.CODE,
        message: API_RESPONSE.ERROR.USERS.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== UPDATE USER ======
  async update(dto: UpdateUser): Promise<ApiResponseInterface> {
    try {
      const user = await this.usersRepo.findOne({ where: { id: dto.id } });
      if (!user) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.USERS.UPDATE.CODE,
          message: API_RESPONSE.ERROR.USERS.UPDATE.MESSAGE,
          data: null,
        };
      }

      const updateData: any = prepareUpdateFields(dto, { updated_by_id: 'updated_by' });

      // Map relations
      if (dto.attachment_signature_id)
        updateData.attachment_signature = { id: dto.attachment_signature_id };
      if (dto.attachment_avatar_id)
        updateData.attachment_avatar = { id: dto.attachment_avatar_id };

      Object.assign(user, updateData);
      await this.usersRepo.save(user);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.USERS.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.USERS.UPDATE.MESSAGE,
        data: user,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.USERS.UPDATE.CODE,
        message: API_RESPONSE.ERROR.USERS.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== SOFT DELETE USER ======
  async softDelete(dto: UpdateUser): Promise<ApiResponseInterface> {
    try {
      const user = await this.usersRepo.findOne({ where: { id: dto.id } });
      if (!user) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.USERS.DELETE.CODE,
          message: API_RESPONSE.ERROR.USERS.DELETE.MESSAGE,
          data: null,
        };
      }

      // Soft delete by setting status to inactive/deleted
      user.status = UserStatus.DELETED;

      await this.usersRepo.save(user);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.USERS.DELETE.CODE,
        message: API_RESPONSE.SUCCESS.USERS.DELETE.MESSAGE,
        data: user,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.USERS.DELETE.CODE,
        message: API_RESPONSE.ERROR.USERS.DELETE.MESSAGE,
        data: error,
      };
    }
  }
}